import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import LoadingDNA from '../components/LoadingDNA';
import { employeeMenuItems } from '../components/employee/employeeMenu';
import { managerMenuItems } from '../components/manager/managerMenu';
import {
  TripRecord,
  approveTrip,
  createTrip,
  deleteTrip,
  getTrips,
  isApiError,
  submitTrip,
  updateTrip,
} from '../services/dashboardService';

const statusClass = (status?: string): string => {
  const value = String(status || '').toLowerCase();
  if (value === 'approved' || value === 'active') return 'bg-emerald-100 text-emerald-800';
  if (value === 'rejected') return 'bg-rose-100 text-rose-800';
  if (value === 'draft') return 'bg-slate-100 text-slate-700';
  return 'bg-amber-100 text-amber-800';
};

const formatDate = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatINR = (amount?: number): string => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

const emptyForm = {
  destination: '',
  purpose: '',
  start_date: '',
  end_date: '',
  estimated_budget: '',
};

const TripsWorkspacePage: React.FC = () => {
  const location = useLocation();
  const isManagerView = location.pathname.startsWith('/manager/');
  const [rows, setRows] = useState<TripRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TripRecord | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const list = await getTrips({ perPage: 500 });
      setRows(list);
    } catch (error) {
      if (isApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to load trips.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const totals = useMemo(() => {
    const pending = rows.filter((trip) => String(trip.status || '').toLowerCase() === 'pending').length;
    const approved = rows.filter((trip) => ['approved', 'active'].includes(String(trip.status || '').toLowerCase())).length;
    const budget = rows.reduce((sum, row) => sum + Number(row.estimated_budget || 0), 0);
    return { total: rows.length, pending, approved, budget };
  }, [rows]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (trip: TripRecord) => {
    setEditing(trip);
    setForm({
      destination: trip.destination || '',
      purpose: trip.purpose || '',
      start_date: String(trip.start_date || '').slice(0, 10),
      end_date: String(trip.end_date || '').slice(0, 10),
      estimated_budget: String(Math.round(Number(trip.estimated_budget || 0))),
    });
    setShowForm(true);
  };

  const onSave = async () => {
    const budget = Number(form.estimated_budget || 0);
    if (!form.destination.trim() || !form.purpose.trim() || !form.start_date || !form.end_date || !Number.isFinite(budget) || budget <= 0) {
      setErrorMessage('Please fill all fields with valid values.');
      return;
    }
    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      if (editing) {
        await updateTrip(editing.id, {
          destination: form.destination.trim(),
          purpose: form.purpose.trim(),
          start_date: form.start_date,
          end_date: form.end_date,
          estimated_budget: budget,
        });
        setActionMessage('Trip updated.');
      } else {
        await createTrip({
          destination: form.destination.trim(),
          purpose: form.purpose.trim(),
          start_date: form.start_date,
          end_date: form.end_date,
          estimated_budget: budget,
        });
        setActionMessage('Trip created.');
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      await load();
    } catch (error) {
      if (isApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to save trip.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async (tripId: string) => {
    try {
      setErrorMessage(null);
      await deleteTrip(tripId);
      setActionMessage('Trip deleted.');
      await load();
    } catch (error) {
      if (isApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to delete trip.');
    }
  };

  const onSubmitTrip = async (tripId: string) => {
    try {
      setErrorMessage(null);
      await submitTrip(tripId);
      setActionMessage('Trip submitted for approval.');
      await load();
    } catch (error) {
      if (isApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to submit trip.');
    }
  };

  const onManagerDecision = async (tripId: string, action: 'approve' | 'reject') => {
    try {
      setErrorMessage(null);
      await approveTrip(tripId, action, action === 'approve' ? 'Approved from manager trips page' : 'Rejected from manager trips page');
      setActionMessage(action === 'approve' ? 'Trip approved.' : 'Trip rejected.');
      await load();
    } catch (error) {
      if (isApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to apply decision.');
    }
  };

  return (
    <AdminShell
      pageTitle="Trips Workspace"
      subtitle={isManagerView ? 'Review and manage trip requests.' : 'Create and track your trip lifecycle.'}
      menuItems={isManagerView ? managerMenuItems : employeeMenuItems}
      profileName={isManagerView ? 'Approving Manager' : 'Employee'}
      profileRoleTag={isManagerView ? 'MANAGER' : 'EMPLOYEE'}
    >
      {errorMessage ? (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-[14px] font-semibold text-red-700">{errorMessage}</p>
      ) : null}
      {actionMessage ? (
        <p className="rounded-lg bg-emerald-50 px-4 py-3 text-[14px] font-semibold text-emerald-700">{actionMessage}</p>
      ) : null}

      {isLoading ? <LoadingDNA /> : null}

      {!isLoading ? (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-[30px] font-extrabold tracking-[-0.02em] text-[#0b5f77]">Trips</h1>
              <p className="text-[14px] font-semibold text-[#6e8ba0]">Plan, submit, and monitor trip approvals.</p>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="h-11 rounded-full bg-[#0d869f] px-5 text-[14px] font-extrabold text-white shadow-[0_10px_20px_rgba(13,113,143,0.25)]"
            >
              + New Trip
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <article className="rounded-[14px] border border-[#dcebf3] bg-white px-4 py-3">
              <p className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">TOTAL TRIPS</p>
              <p className="mt-1 text-[28px] font-extrabold text-[#0b5f77]">{totals.total}</p>
            </article>
            <article className="rounded-[14px] border border-[#dcebf3] bg-white px-4 py-3">
              <p className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">PENDING APPROVAL</p>
              <p className="mt-1 text-[28px] font-extrabold text-[#0b708b]">{totals.pending}</p>
            </article>
            <article className="rounded-[14px] border border-[#dcebf3] bg-white px-4 py-3">
              <p className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">DECLARED BUDGET</p>
              <p className="mt-1 text-[28px] font-extrabold text-[#0b7f59]">{formatINR(totals.budget)}</p>
            </article>
          </div>

          {showForm ? (
            <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
              <h2 className="text-[20px] font-extrabold text-[#0b5f77]">{editing ? 'Edit Trip' : 'Create Trip'}</h2>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <input
                  className="h-11 rounded-[10px] border border-[#d7e6ef] px-3 text-[14px] font-semibold text-[#30576c]"
                  placeholder="Destination"
                  value={form.destination}
                  onChange={(e) => setForm((prev) => ({ ...prev, destination: e.target.value }))}
                />
                <input
                  className="h-11 rounded-[10px] border border-[#d7e6ef] px-3 text-[14px] font-semibold text-[#30576c]"
                  placeholder="Purpose"
                  value={form.purpose}
                  onChange={(e) => setForm((prev) => ({ ...prev, purpose: e.target.value }))}
                />
                <input
                  type="date"
                  className="h-11 rounded-[10px] border border-[#d7e6ef] px-3 text-[14px] font-semibold text-[#30576c]"
                  value={form.start_date}
                  onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value }))}
                />
                <input
                  type="date"
                  className="h-11 rounded-[10px] border border-[#d7e6ef] px-3 text-[14px] font-semibold text-[#30576c]"
                  value={form.end_date}
                  onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.target.value }))}
                />
                <input
                  type="number"
                  min={1}
                  className="h-11 rounded-[10px] border border-[#d7e6ef] px-3 text-[14px] font-semibold text-[#30576c]"
                  placeholder="Estimated Budget"
                  value={form.estimated_budget}
                  onChange={(e) => setForm((prev) => ({ ...prev, estimated_budget: e.target.value }))}
                />
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={onSave}
                  disabled={isSubmitting}
                  className="h-10 rounded-full bg-[#0d869f] px-5 text-[13px] font-extrabold text-white disabled:opacity-60"
                >
                  {isSubmitting ? 'Saving...' : editing ? 'Save Changes' : 'Create Trip'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditing(null);
                    setForm(emptyForm);
                  }}
                  className="h-10 rounded-full border border-[#c0d8e6] px-5 text-[13px] font-extrabold text-[#24556e]"
                >
                  Cancel
                </button>
              </div>
            </article>
          ) : null}

          <article className="rounded-[16px] border border-[#deebf2] bg-white">
            <div className="border-b border-[#deebf2] bg-[#edf7fb] px-5 py-4">
              <h2 className="text-[22px] font-extrabold tracking-[-0.02em] text-[#0b5f77]">Trip List</h2>
            </div>
            {rows.length === 0 ? (
              <p className="px-5 py-6 text-[14px] font-semibold text-[#6f8899]">No trips found.</p>
            ) : (
              <div className="divide-y divide-[#e7f1f6]">
                {rows.map((trip) => {
                  const status = String(trip.status || 'draft').toLowerCase();
                  const canEdit = status === 'draft';
                  const canSubmit = status === 'draft';
                  const canManagerDecision = isManagerView && status === 'pending';
                  return (
                    <div key={trip.id} className="px-5 py-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-[20px] font-extrabold text-[#0f5f78]">{trip.destination || 'Trip'}</p>
                          <p className="mt-1 text-[14px] font-semibold text-[#658496]">{trip.purpose || '-'}</p>
                          <p className="mt-1 text-[13px] font-semibold text-[#6f8ca0]">
                            {formatDate(trip.start_date)} - {formatDate(trip.end_date)} • Budget {formatINR(trip.estimated_budget)}
                          </p>
                          <p className="mt-1 text-[12px] font-semibold text-[#89a4b6]">Trip ID: {trip.id}</p>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-[11px] font-extrabold tracking-[0.08em] ${statusClass(status)}`}>
                          {status.toUpperCase()}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => openEdit(trip)}
                            className="rounded-full border border-[#b8d6e4] px-4 py-1.5 text-[12px] font-bold text-[#0b6f89]"
                          >
                            Edit
                          </button>
                        ) : null}
                        {canSubmit ? (
                          <button
                            type="button"
                            onClick={() => onSubmitTrip(trip.id)}
                            className="rounded-full bg-[#0d90aa] px-4 py-1.5 text-[12px] font-bold text-white"
                          >
                            Submit
                          </button>
                        ) : null}
                        {canEdit ? (
                          <button
                            type="button"
                            onClick={() => onDelete(trip.id)}
                            className="rounded-full border border-[#efc7c7] px-4 py-1.5 text-[12px] font-bold text-[#c75a5a]"
                          >
                            Delete
                          </button>
                        ) : null}
                        {canManagerDecision ? (
                          <>
                            <button
                              type="button"
                              onClick={() => onManagerDecision(trip.id, 'approve')}
                              className="rounded-full bg-[#0d90aa] px-4 py-1.5 text-[12px] font-bold text-white"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => onManagerDecision(trip.id, 'reject')}
                              className="rounded-full border border-[#efc7c7] px-4 py-1.5 text-[12px] font-bold text-[#c75a5a]"
                            >
                              Reject
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </article>
        </>
      ) : null}
    </AdminShell>
  );
};

export default TripsWorkspacePage;

